import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { AlertTriangle, Ban, BookOpen, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown, ExternalLink, GraduationCap, Link2, Loader2, RotateCcw, School as SchoolIcon, Search, ShieldCheck, ShieldOff, Trash2, User, Users, X } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import {
  userManagementService,
  type OnboardingStatus,
  type SchoolRoleValue,
  type UserListItem,
  type UserRole,
  type UserSortBy,
  type UserStatus,
} from '../services/userManagementService';
import { schoolService } from '../services/schoolService';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The DB role change succeeded, but it could not be mirrored to Clerk —
 * i.e. the user's stored Clerk identity (auth_user_id) is missing or stale
 * (classic cause: their Clerk account was deleted & recreated). The role
 * won't take effect at sign-in until that's reconciled, so make this loud
 * and non-auto-dismissing rather than letting it pass as a success.
 */
function notifyClerkSync(updated: UserListItem): void {
  if (updated.clerkSync && updated.clerkSync !== 'ok') {
    toast.warn(
      updated.clerkSyncMessage ||
        'Role saved to the database, but it was NOT synced to Clerk. The user may be unable to sign in (or stay wrongly elevated) until their Clerk identity is verified against users.auth_user_id.',
      { autoClose: false },
    );
  }
}

const ROLE_FILTERS: { key: UserRole | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All roles' },
  { key: 'STUDENT', label: 'Students' },
  { key: 'SCHOOL_ADMIN', label: 'School admins' },
  { key: 'ADMIN', label: 'Hypebook admins' },
];

const STATUS_FILTERS: { key: UserStatus | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All status' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'INACTIVE', label: 'Inactive' },
  { key: 'SUSPENDED', label: 'Suspended' },
  { key: 'DELETED', label: 'Deleted' },
];

// Public-profile link target — the Student app is on its own domain. We
// surface this from an env var so the same Platform-Admin bundle works in
// dev and prod without code changes. NOTE: the var is VITE_STUDENT_URL
// (defined in .env* alongside VITE_SCHOOL_ADMIN_URL) — an earlier
// VITE_STUDENT_APP_URL reference here was never defined, so this was always
// empty and the public-profile link silently never rendered.
const STUDENT_APP_URL = (import.meta.env.VITE_STUDENT_URL as string | undefined) ?? '';

// Build the absolute URL to a user's public "hypebook" (their public
// profile). The canonical route is /u/<slug> — NOT /<slug> (that 404s).
// Returns null when we can't link: no slug (no public handle set) or no
// configured Student app URL.
//
// The env var is stored scheme-less in prod (e.g. "hypebook-student.vercel.app"
// — see the sibling VITE_SCHOOL_ADMIN_URL) but with a scheme in dev
// ("http://localhost:3000"). A scheme-less href would resolve relative to the
// admin domain and 404, so prepend https:// when no scheme is present.
function hypebookUrl(slug: string | null | undefined): string | null {
  if (!STUDENT_APP_URL || !slug) return null;
  const base = STUDENT_APP_URL.replace(/\/+$/, '');
  const withScheme = /^https?:\/\//i.test(base) ? base : `https://${base}`;
  return `${withScheme}/u/${slug}`;
}

const ROLE_BADGE: Record<UserRole, string> = {
  ADMIN: 'bg-amber-100 text-amber-800',
  SCHOOL_ADMIN: 'bg-blue-100 text-blue-800',
  STUDENT: 'bg-gray-100 text-gray-700',
};

const STATUS_BADGE: Record<UserStatus, string> = {
  ACTIVE: 'bg-green-50 text-green-700',
  INACTIVE: 'bg-gray-100 text-gray-600',
  SUSPENDED: 'bg-red-50 text-red-700',
  DELETED: 'bg-gray-200 text-gray-500',
};

const ONBOARDING_LABEL: Record<OnboardingStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
};

function fullName(u: UserListItem): string {
  const composed = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return composed.length > 0 ? composed : '—';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function Pill({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide ${tone}`}>{children}</span>;
}

interface SortHeaderProps {
  label: string;
  sortKey: UserSortBy;
  sortBy: UserSortBy;
  sortDir: 'asc' | 'desc';
  onSort: (key: UserSortBy) => void;
  align?: 'right';
}

/**
 * Clickable column header. Sorting is server-side (see listUsers), so it
 * reorders the entire result set, not just the visible page. Shows a
 * directional chevron on the active column and a faint hint on the rest.
 */
function SortHeader({ label, sortKey, sortBy, sortDir, onSort, align }: SortHeaderProps) {
  const isActive = sortBy === sortKey;
  return (
    <th className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label}`}
        aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`group inline-flex items-center gap-1 uppercase tracking-wider hover:text-primary transition-colors ${
          isActive ? 'text-primary' : ''
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {label}
        {isActive ? (
          sortDir === 'asc' ? (
            <ChevronUp className="w-3 h-3 shrink-0" />
          ) : (
            <ChevronDown className="w-3 h-3 shrink-0" />
          )
        ) : (
          <ChevronsUpDown className="w-3 h-3 shrink-0 text-gray-300 group-hover:text-primary/60" />
        )}
      </button>
    </th>
  );
}

export default function ManageUsersPage() {
  const { user: clerkUser } = useUser();
  // Self-identification for the self-demote guard. The server also enforces
  // this (we can't trust the client) — comparing email here is purely a UX
  // hint so the button is disabled with a tooltip instead of letting the
  // admin click and immediately get a 400. Clerk's primary email matches
  // the User.email value in our DB (Clerk is the source of truth for both).
  const currentEmail = (clerkUser?.primaryEmailAddress?.emailAddress ?? '').toLowerCase();

  // Cards on /manage-schools deep-link here pre-filtered (e.g. "Total
  // Students" → STUDENT). The router state is one-shot: we consume it on
  // mount then clear it so a later navigate('/manage-users') without
  // state doesn't accidentally re-apply a stale filter.
  const location = useLocation();
  const navigate = useNavigate();
  const incomingRoleFilter =
    (location.state as { roleFilter?: UserRole | 'ALL' } | null)?.roleFilter ?? null;

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | 'ALL'>(incomingRoleFilter ?? 'ALL');
  const [status, setStatus] = useState<UserStatus | 'ALL'>('ALL');
  // Column sort. Defaults to the server's own default (newest first) so
  // the initial render matches an unsorted request.
  const [sortBy, setSortBy] = useState<UserSortBy>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Per-row pending state for the role-change button so a slow request
  // spinners just that row's button instead of locking the whole list.
  const [roleChanging, setRoleChanging] = useState<Set<string>>(new Set());
  // Same per-row spinner pattern for status/relink (block/unblock/delete/relink).
  const [statusChanging, setStatusChanging] = useState<Set<string>>(new Set());
  // School-admin picker modal: open with the user we're promoting; null
  // when closed. The modal handles its own school search + role select.
  const [schoolPickerFor, setSchoolPickerFor] = useState<UserListItem | null>(null);

  // Strip the one-shot router state once we've used it. Without this,
  // back-navigation or a re-render with the same key would keep
  // reapplying the prefilter. replace: true so we don't pollute history.
  useEffect(() => {
    if (incomingRoleFilter) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // Run exactly once on mount: incomingRoleFilter is derived from
    // location.state which we're about to wipe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search input. Resets to page 1 since a new query invalidates
  // the previous offset.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Race-safety: a fresh load() call bumps the token; only the latest
  // response is allowed to mutate state. Without this, rapid filter
  // changes can leave a stale page visible after the new request returns
  // slower.
  const loadTokenRef = useRef(0);

  const load = useCallback(async () => {
    const myToken = ++loadTokenRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await userManagementService.listUsers({
        q: search || undefined,
        role: role === 'ALL' ? undefined : role,
        status: status === 'ALL' ? undefined : status,
        sortBy,
        sortDir,
        page,
        pageSize: PAGE_SIZE,
      });
      if (myToken !== loadTokenRef.current) return;
      setUsers(result.users);
      setTotal(result.total);
    } catch (e) {
      if (myToken !== loadTokenRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      if (myToken === loadTokenRef.current) setIsLoading(false);
    }
  }, [search, role, status, sortBy, sortDir, page]);

  useEffect(() => {
    void load();
  }, [load]);

  // Clicking a header sorts by that column. Re-clicking the active column
  // flips direction; switching columns starts at the natural default —
  // newest-first for the Joined column, A→Z for everything else. Snaps
  // back to page 1 since the previous offset no longer applies.
  const toggleSort = useCallback(
    (key: UserSortBy) => {
      if (sortBy === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(key);
        setSortDir(key === 'createdAt' ? 'desc' : 'asc');
      }
      setPage(1);
    },
    [sortBy],
  );

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Promote STUDENT → ADMIN, demote ADMIN → STUDENT, or revoke
  // SCHOOL_ADMIN back to STUDENT. SCHOOL_ADMIN promotion goes through
  // its own picker modal (handleSchoolAdminPromote) since it needs
  // schoolId + schoolRole.
  const handleRoleToggle = async (target: UserListItem) => {
    let nextRole: 'STUDENT' | 'ADMIN';
    let confirmMsg: string;
    let successMsg: string;
    if (target.role === 'ADMIN') {
      nextRole = 'STUDENT';
      confirmMsg = `Revoke Hypebook admin access from ${target.email}?`;
      successMsg = `Revoked admin from ${target.email}`;
    } else if (target.role === 'SCHOOL_ADMIN') {
      // Demote SCHOOL_ADMIN → STUDENT. Server deactivates the user's
      // SchoolMembership rows in the same transaction.
      nextRole = 'STUDENT';
      confirmMsg = `Revoke school admin access from ${target.email}? Their school membership will be deactivated.`;
      successMsg = `Revoked school admin from ${target.email}`;
    } else {
      // STUDENT → ADMIN (platform admin). SCHOOL_ADMIN promotion uses
      // the dedicated picker — see handleSchoolAdminPromote.
      nextRole = 'ADMIN';
      confirmMsg = `Grant Hypebook admin access to ${target.email}?`;
      successMsg = `Granted admin to ${target.email}`;
    }
    if (!window.confirm(confirmMsg)) return;

    setRoleChanging((prev) => new Set(prev).add(target.id));
    const previousRole = target.role;
    setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, role: nextRole } : u)));
    try {
      const updated = await userManagementService.updateUserRole(target.id, { role: nextRole });
      setUsers((prev) => prev.map((u) => (u.id === target.id ? updated : u)));
      toast.success(successMsg);
      notifyClerkSync(updated);
    } catch (e) {
      setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, role: previousRole } : u)));
      toast.error(e instanceof Error ? e.message : 'Role update failed');
    } finally {
      setRoleChanging((prev) => {
        const next = new Set(prev);
        next.delete(target.id);
        return next;
      });
    }
  };

  // Called by the school-picker modal after the admin confirms which
  // school + role. Same optimistic+revert pattern as handleRoleToggle.
  const handleSchoolAdminPromote = async (
    target: UserListItem,
    schoolId: string,
    schoolName: string,
    schoolRole: SchoolRoleValue,
  ) => {
    setRoleChanging((prev) => new Set(prev).add(target.id));
    const previous = { role: target.role, schoolName: target.schoolName };
    setUsers((prev) =>
      prev.map((u) => (u.id === target.id ? { ...u, role: 'SCHOOL_ADMIN', schoolName } : u)),
    );
    try {
      const updated = await userManagementService.updateUserRole(target.id, {
        role: 'SCHOOL_ADMIN',
        schoolId,
        schoolRole,
      });
      setUsers((prev) => prev.map((u) => (u.id === target.id ? updated : u)));
      toast.success(`Made ${target.email} a school admin at ${schoolName}`);
      notifyClerkSync(updated);
      setSchoolPickerFor(null);
    } catch (e) {
      setUsers((prev) =>
        prev.map((u) => (u.id === target.id ? { ...u, role: previous.role, schoolName: previous.schoolName } : u)),
      );
      toast.error(e instanceof Error ? e.message : 'School admin update failed');
    } finally {
      setRoleChanging((prev) => {
        const next = new Set(prev);
        next.delete(target.id);
        return next;
      });
    }
  };

  // Block / unblock / soft-delete / restore. Same optimistic+revert +
  // clerkSync-warning pattern as the role actions.
  const handleStatusChange = async (target: UserListItem, nextStatus: 'ACTIVE' | 'SUSPENDED' | 'DELETED') => {
    if (nextStatus !== 'ACTIVE') {
      const action = nextStatus === 'SUSPENDED' ? 'Block' : 'Soft-delete';
      const consequence =
        nextStatus === 'SUSPENDED'
          ? "They'll be signed out immediately and can't sign in until unblocked."
          : "They'll be signed out and hidden from the product until restored. Their data is kept.";
      if (!window.confirm(`${action} ${target.email}?\n\n${consequence}`)) return;
    }
    setStatusChanging((prev) => new Set(prev).add(target.id));
    const previousStatus = target.status;
    setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, status: nextStatus } : u)));
    try {
      const updated = await userManagementService.updateUserStatus(target.id, nextStatus);
      setUsers((prev) => prev.map((u) => (u.id === target.id ? updated : u)));
      const verb = nextStatus === 'ACTIVE' ? 'reactivated' : nextStatus === 'SUSPENDED' ? 'blocked' : 'soft-deleted';
      toast.success(`${target.email} ${verb}`);
      notifyClerkSync(updated);
    } catch (e) {
      setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, status: previousStatus } : u)));
      toast.error(e instanceof Error ? e.message : 'Status update failed');
    } finally {
      setStatusChanging((prev) => {
        const next = new Set(prev);
        next.delete(target.id);
        return next;
      });
    }
  };

  // Repoint auth_user_id to the user's current Clerk identity (the
  // automated form of the manual relink SQL). Prompt is fail-safe: the
  // server also validates the `user_` prefix and collision.
  const handleRelink = async (target: UserListItem) => {
    const clerkUserId = window
      .prompt(`Relink ${target.email} to a Clerk identity.\n\nPaste their CURRENT Clerk User ID from the Clerk Dashboard (starts with "user_"):`)
      ?.trim();
    if (!clerkUserId) return;
    if (!clerkUserId.startsWith('user_')) {
      toast.error('That is not a Clerk User ID — it must start with "user_". Copy it from the Clerk Dashboard.');
      return;
    }
    setStatusChanging((prev) => new Set(prev).add(target.id));
    try {
      const updated = await userManagementService.relinkClerk(target.id, clerkUserId);
      setUsers((prev) => prev.map((u) => (u.id === target.id ? updated : u)));
      toast.success(`${target.email}: Clerk identity relinked`);
      notifyClerkSync(updated);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Relink failed');
    } finally {
      setStatusChanging((prev) => {
        const next = new Set(prev);
        next.delete(target.id);
        return next;
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-50 font-Manrope">
      <Sidebar />
      <main className="ml-[230px] p-8">
        <div className="max-w-6xl mx-auto">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Users</h1>
            <p className="text-gray-500 mt-1">
              Read-only directory of every account on the platform. Click a row to expand details. Actions (suspend, promote, delete) come in v2.
            </p>
          </header>

          {/* Filter bar */}
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name or email"
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={role}
                onChange={(e) => {
                  setRole(e.target.value as UserRole | 'ALL');
                  setPage(1);
                }}
                className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-primary"
              >
                {ROLE_FILTERS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as UserStatus | 'ALL');
                  setPage(1);
                }}
                className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-primary"
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Result count */}
          <div className="flex items-center justify-between text-sm text-gray-500 mb-3 px-1">
            <span>
              {isLoading ? (
                'Loading…'
              ) : (
                <>
                  <span className="font-semibold text-gray-700">{total.toLocaleString()}</span> {total === 1 ? 'user' : 'users'}
                  {search || role !== 'ALL' || status !== 'ALL' ? ' matching filters' : ''}
                </>
              )}
            </span>
            {totalPages > 1 && !isLoading && (
              <span>
                Page {page} of {totalPages}
              </span>
            )}
          </div>

          {/* Table */}
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Couldn't load users</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-24 bg-white rounded-2xl border border-gray-100">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-700 font-semibold">No users match</p>
              <p className="text-sm text-gray-500 mt-1">Try clearing search or filters.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-8" />
                    <SortHeader label="Name" sortKey="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Email" sortKey="email" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Role" sortKey="role" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Status" sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="School" sortKey="school" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Joined" sortKey="createdAt" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isOpen = expanded.has(u.id);
                    return (
                      <Fragment key={u.id}>
                        <tr
                          onClick={() => toggleExpanded(u.id)}
                          className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3">
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {u.role === 'ADMIN' ? (
                                <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0" />
                              ) : (
                                <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              )}
                              <span className="text-sm font-semibold text-gray-900">{fullName(u)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{u.email}</td>
                          <td className="px-4 py-3">
                            <Pill tone={ROLE_BADGE[u.role]}>{u.role.replace('_', ' ')}</Pill>
                          </td>
                          <td className="px-4 py-3">
                            <Pill tone={STATUS_BADGE[u.status]}>{u.status}</Pill>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-[180px] truncate">{u.schoolName ?? '—'}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-500">{formatDate(u.createdAt)}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-blue-50/30 border-b border-gray-50">
                            <td />
                            <td colSpan={6} className="px-4 py-4">
                              {/* Hypebook/onboarding/achievements are STUDENT
                                  concepts. Staff rows get a slug at signup but
                                  never a hypepage, so /u/<slug> 404s — showing
                                  the button and link for them advertised a
                                  broken page. Student-only fields are hidden
                                  for ADMIN / SCHOOL_ADMIN. */}
                              {u.role === 'STUDENT' && (
                                <div className="flex justify-end mb-3">
                                  <ViewHypebookButton user={u} />
                                </div>
                              )}
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                                <DetailField label="User ID" value={u.id} mono />
                                {u.role === 'STUDENT' && (
                                  <>
                                    <DetailField
                                      label="Onboarding"
                                      value={u.onboardingStatus ? ONBOARDING_LABEL[u.onboardingStatus] : '—'}
                                    />
                                    <DetailField label="Achievements" value={u.achievementsCount.toLocaleString()} />
                                    <DetailField
                                      label="Public profile"
                                      value={
                                        hypebookUrl(u.slug) ? (
                                          <a
                                            href={hypebookUrl(u.slug) as string}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1 text-primary hover:underline"
                                          >
                                            /u/{u.slug}
                                            <ExternalLink className="w-3 h-3" />
                                          </a>
                                        ) : u.slug ? (
                                          <span className="font-mono text-xs text-gray-700">/u/{u.slug}</span>
                                        ) : (
                                          '—'
                                        )
                                      }
                                    />
                                  </>
                                )}
                              </div>
                              <RoleActionRow
                                user={u}
                                isCurrentUser={currentEmail !== '' && u.email.toLowerCase() === currentEmail}
                                isPending={roleChanging.has(u.id)}
                                onToggle={() => void handleRoleToggle(u)}
                                onMakeSchoolAdmin={() => setSchoolPickerFor(u)}
                              />
                              <AccountActions
                                user={u}
                                isCurrentUser={currentEmail !== '' && u.email.toLowerCase() === currentEmail}
                                isPending={statusChanging.has(u.id)}
                                onSetStatus={(s) => void handleStatusChange(u, s)}
                                onRelink={() => void handleRelink(u)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Prev
                  </button>
                  <span className="text-sm text-gray-500">
                    Page <span className="font-semibold text-gray-700">{page}</span> of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      {schoolPickerFor && (
        <SchoolPickerModal
          user={schoolPickerFor}
          isPending={roleChanging.has(schoolPickerFor.id)}
          onClose={() => setSchoolPickerFor(null)}
          onConfirm={(schoolId, schoolName, schoolRole) => {
            const user = schoolPickerFor;
            if (!user) return;
            void handleSchoolAdminPromote(user, schoolId, schoolName, schoolRole);
          }}
        />
      )}
    </div>
  );
}

function DetailField({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`text-sm text-gray-800 mt-0.5 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</p>
    </div>
  );
}

/**
 * Opens the user's public "hypebook" (public profile at /u/<slug>) in a new
 * tab. This is the public view — it only resolves for users who have set a
 * public handle (slug) AND whose page isn't private (private pages 403 to
 * anonymous viewers). When there's no linkable profile we render a disabled
 * button with a tooltip explaining why, so every row still shows the action.
 */
function ViewHypebookButton({ user }: { user: UserListItem }) {
  const url = hypebookUrl(user.slug);
  const base =
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors border';

  if (!url) {
    const reason = !STUDENT_APP_URL
      ? 'Student app URL is not configured for this environment.'
      : "This user has no public profile yet (no public handle set).";
    return (
      <span
        title={reason}
        aria-disabled="true"
        className={`${base} bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed`}
      >
        <BookOpen className="w-4 h-4" />
        View Hypebook
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open this user's public Hypebook in a new tab"
      className={`${base} bg-primary text-white border-primary hover:bg-primary-600`}
    >
      <BookOpen className="w-4 h-4" />
      View Hypebook
      <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
}

interface RoleActionRowProps {
  user: UserListItem;
  /** True when this row represents the currently signed-in admin. We
   *  disable the demote button so they can't lock themselves out. */
  isCurrentUser: boolean;
  isPending: boolean;
  onToggle: () => void;
  /** Opens the school-picker modal. Only relevant for STUDENT/ADMIN
   *  rows — SCHOOL_ADMIN rows get a single Revoke button instead. */
  onMakeSchoolAdmin: () => void;
}

function RoleActionRow({ user, isCurrentUser, isPending, onToggle, onMakeSchoolAdmin }: RoleActionRowProps) {
  // SCHOOL_ADMIN row — one button: Revoke, which deactivates the
  // SchoolMembership server-side and drops the user back to STUDENT.
  if (user.role === 'SCHOOL_ADMIN') {
    return (
      <div className="mt-4 pt-4 border-t border-blue-100 flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Actions</p>
          <p className="text-sm text-gray-700">
            School admin{user.schoolName ? ` at ${user.schoolName}` : ''}.
          </p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
        >
          {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
          Revoke school admin
        </button>
      </div>
    );
  }

  const isAdmin = user.role === 'ADMIN';
  // Self-demote guard: server rejects too, but disabling client-side
  // spares the admin a 400 round-trip and makes the rule visible.
  const blockSelfDemote = isCurrentUser && isAdmin;

  return (
    <div className="mt-4 pt-4 border-t border-blue-100 flex items-center justify-between gap-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Actions</p>
        <p className="text-sm text-gray-700">
          {isAdmin ? 'Has Hypebook admin access — can sign in to this panel.' : 'Standard student account.'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onMakeSchoolAdmin}
          disabled={isPending}
          title="Promote to school admin at a specific school"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
        >
          <GraduationCap className="w-4 h-4" />
          Make school admin
        </button>
        <button
          type="button"
          onClick={onToggle}
          disabled={isPending || blockSelfDemote}
          title={
            blockSelfDemote
              ? "You can't remove your own admin role — ask another admin"
              : isAdmin
                ? 'Revoke Hypebook admin access'
                : 'Grant Hypebook admin access'
          }
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isAdmin
              ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
              : 'bg-amber-500 text-white hover:bg-amber-600'
          }`}
        >
          {isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isAdmin ? (
            <ShieldOff className="w-4 h-4" />
          ) : (
            <ShieldCheck className="w-4 h-4" />
          )}
          {isAdmin ? 'Revoke admin' : 'Make admin'}
        </button>
      </div>
    </div>
  );
}

interface AccountActionsProps {
  user: UserListItem;
  isCurrentUser: boolean;
  isPending: boolean;
  onSetStatus: (status: 'ACTIVE' | 'SUSPENDED' | 'DELETED') => void;
  onRelink: () => void;
}

/**
 * Account lifecycle actions (separate from role): block/unblock,
 * soft-delete/restore, and relink Clerk identity. Enforced server-side by
 * the auth guard; block/delete also Clerk-ban + revoke sessions.
 */
function AccountActions({ user, isCurrentUser, isPending, onSetStatus, onRelink }: AccountActionsProps) {
  const blocked = user.status === 'SUSPENDED';
  const deleted = user.status === 'DELETED';
  const selfBlock = isCurrentUser ? "You can't block or delete your own account" : undefined;
  const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border';

  return (
    <div className="mt-3 pt-3 border-t border-blue-100 flex items-center justify-between gap-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Account</p>
        <p className="text-sm text-gray-700">
          Status: <span className="font-medium">{user.status}</span>
          {deleted ? ' — soft-deleted (data kept).' : blocked ? ' — blocked from signing in.' : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {isPending ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : null}
        <button
          type="button"
          onClick={onRelink}
          disabled={isPending}
          title="Repoint this user to their current Clerk identity (recreated/duplicate Clerk account)"
          className={`${btn} bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200`}
        >
          <Link2 className="w-4 h-4" />
          Relink Clerk ID
        </button>
        {deleted || blocked ? (
          <button
            type="button"
            onClick={() => onSetStatus('ACTIVE')}
            disabled={isPending}
            title={deleted ? 'Restore this account' : 'Unblock — allow sign-in again'}
            className={`${btn} bg-green-50 text-green-700 hover:bg-green-100 border-green-200`}
          >
            <RotateCcw className="w-4 h-4" />
            {deleted ? 'Restore' : 'Unblock'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onSetStatus('SUSPENDED')}
            disabled={isPending || isCurrentUser}
            title={selfBlock ?? 'Block sign-in (Clerk-ban + revoke sessions)'}
            className={`${btn} bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200`}
          >
            <Ban className="w-4 h-4" />
            Block
          </button>
        )}
        {!deleted ? (
          <button
            type="button"
            onClick={() => onSetStatus('DELETED')}
            disabled={isPending || isCurrentUser}
            title={selfBlock ?? 'Soft-delete (reversible — data kept)'}
            className={`${btn} bg-red-50 text-red-700 hover:bg-red-100 border-red-200`}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── School picker modal ──────────────────────────────────────────────────

const SCHOOL_ROLE_OPTIONS: { value: SchoolRoleValue; label: string; description: string }[] = [
  { value: 'ADMIN', label: 'Admin', description: 'Default — manage students and staff at this school.' },
  { value: 'OWNER', label: 'Owner', description: 'Highest authority. Becomes the school’s schoolAdminId if unset.' },
  { value: 'BILLING', label: 'Billing', description: 'Billing and subscription access.' },
  { value: 'COUNSELOR', label: 'Counselor', description: 'Read-mostly access for guidance staff.' },
  { value: 'CLASSROOM_VIEWER', label: 'Teacher', description: 'Teacher — view rosters and issue certificates; limited classroom-level access.' },
];

interface SchoolOption {
  id: string;
  name: string;
}

interface SchoolPickerModalProps {
  user: UserListItem;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (schoolId: string, schoolName: string, schoolRole: SchoolRoleValue) => void;
}

function SchoolPickerModal({ user, isPending, onClose, onConfirm }: SchoolPickerModalProps) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<SchoolOption | null>(null);
  const [schoolRole, setSchoolRole] = useState<SchoolRoleValue>('ADMIN');

  // Debounce search → API call so typing doesn't fire a request per
  // keystroke. 300ms matches the SchoolDirectory page convention.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Race-safety: a fresh load() bumps the token; only the latest is
  // allowed to mutate state. Without this, typing fast can leave stale
  // results visible after the new request returns slower.
  const loadTokenRef = useRef(0);

  useEffect(() => {
    const myToken = ++loadTokenRef.current;
    setIsLoading(true);
    void schoolService
      .getSchools({ search: search || undefined, limit: 20, page: 1 })
      .then((result) => {
        if (myToken !== loadTokenRef.current) return;
        setSchools(result.data.map((s) => ({ id: s.id, name: s.name })));
      })
      .catch(() => {
        if (myToken !== loadTokenRef.current) return;
        setSchools([]);
      })
      .finally(() => {
        if (myToken === loadTokenRef.current) setIsLoading(false);
      });
  }, [search]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Make school admin</h2>
            <p className="text-sm text-gray-500 mt-1">
              Promote <span className="font-semibold text-gray-700">{user.email}</span> to SCHOOL_ADMIN at the chosen school.
            </p>
          </div>
          <button type="button" onClick={onClose} className="w-7 h-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* School search */}
        <div className="mb-4">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">School</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                // Typing clears any current selection — selecting is an
                // explicit click on a suggestion below.
                if (selected) setSelected(null);
              }}
              placeholder="Search schools by name"
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              autoFocus
            />
          </div>

          {/* Suggestion list */}
          <div className="mt-2 max-h-48 overflow-y-auto border border-gray-100 rounded-lg">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              </div>
            ) : schools.length === 0 ? (
              <p className="text-sm text-gray-500 italic px-3 py-4 text-center">No schools match.</p>
            ) : (
              <ul>
                {schools.map((s) => {
                  const isPicked = selected?.id === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(s)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                          isPicked ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <SchoolIcon className={`w-4 h-4 flex-shrink-0 ${isPicked ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="truncate">{s.name}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* School role */}
        <div className="mb-5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Role at school</label>
          <select
            value={schoolRole}
            onChange={(e) => setSchoolRole(e.target.value as SchoolRoleValue)}
            className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-primary"
          >
            {SCHOOL_ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {/* Inline description for the picked role — useful since OWNER
              carries the schoolAdminId side-effect noted in the option list. */}
          <p className="mt-1.5 text-xs text-gray-500">
            {SCHOOL_ROLE_OPTIONS.find((o) => o.value === schoolRole)?.description}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (selected) onConfirm(selected.id, selected.name, schoolRole);
            }}
            disabled={!selected || isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />}
            Make school admin
          </button>
        </div>
      </div>
    </div>
  );
}
